import React, { useEffect, useRef, useMemo, useCallback } from 'react';
import { Message, Reference } from '../types';
import { useAnnouncer } from '../context/AccessibilityContext';
import MessageItem from './MessageItem';
import { groupMessages, getCombinedContent, isGroupStreaming } from '../utils/messageGrouping';
import './MessageList.css';

interface MessageListProps {
  messages: Message[];
  currentUser: string;
  onReferenceClick: (ref: Reference) => void;
}

const MessageList: React.FC<MessageListProps> = React.memo(({
  messages,
  currentUser,
  onReferenceClick
}) => {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const announcer = useAnnouncer();
  const previousMessageCount = useRef(messages.length);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  // Announce new messages to screen readers
  useEffect(() => {
    if (messages.length > previousMessageCount.current) {
      const newMessages = messages.slice(previousMessageCount.current);
      newMessages.forEach(message => {
        if (message.sender !== currentUser) {
          announcer.announceNewMessage(message.sender, message.content);
        }
      });
    }
    previousMessageCount.current = messages.length;
  }, [messages, currentUser, announcer]);

  // Memoize message grouping to avoid recalculation on every render
  const messageGroups = useMemo(() => {
    return groupMessages(messages);
  }, [messages]);

  // Memoize reference click handler to prevent unnecessary re-renders
  const memoizedOnReferenceClick = useCallback((ref: Reference) => {
    onReferenceClick(ref);
  }, [onReferenceClick]);

  return (
    <div className="message-list" ref={containerRef}>
      <div className="message-list__content">
        {messageGroups.map((group, groupIndex) => {
          // Create a combined message for the group
          const representativeMessage = group.messages[group.messages.length - 1];
          const combinedMessage: Message = {
            ...representativeMessage,
            content: getCombinedContent(group.messages),
            thinking: group.combinedThinking,
            isStreaming: isGroupStreaming(group.messages),
          };

          return (
            <div key={group.id} className="message-group">
              <MessageItem
                key={group.id}
                message={combinedMessage}
                isOwn={representativeMessage.sender === currentUser}
                showTimestamp={group.showTimestamp}
                onReferenceClick={memoizedOnReferenceClick}
              />
            </div>
          );
        })}
        <div ref={messagesEndRef} />
      </div>
    </div>
  );
});

export default MessageList;